/**
 * AliffPriorityMapper
 *
 * Utilities for transforming Flux auto-layout priorities to DeepPCB-compatible values.
 *
 * Context:
 * - Flux treats lower priority numbers as higher priority (P=1 > P=2 > P=3)
 * - DeepPCB expects the opposite (lower values are more important)
 * - DeepPCB prefers exponentially-spaced distinct values for better routing results
 *
 * Transformation pipeline:
 * 1. Map unique float priorities to unique integers (preserving order)
 * 2. Apply exponential spacing (EXPONENTIAL_SPACING_FACTOR = 2.5)
 * 3. Invert for DeepPCB's priority order
 */
export class AliffPriorityMapper {
    /**
     * Exponential spacing factor for DeepPCB priority transformation.
     * DeepPCB routing algorithm prefers exponentially-spaced priority values.
     */
    static EXPONENTIAL_SPACING_FACTOR = 2.5;
    /**
     * Maps unique priority values to sequential integers while preserving order.
     *
     * Uses the formula: f(i) = f(i-1) + Math.ceil(x_i - x_(i-1))
     *
     * This ensures that:
     * - Priorities maintain their relative ordering
     * - Gaps between priorities are preserved proportionally
     * - All output values are unique integers
     *
     * @example
     * AliffPriorityMapper.mapUniquePrioritiesToIntegers([0.4, 0.5, 1, 5, 8])
     * // Returns: Map { 0.4 => 1, 0.5 => 2, 1 => 3, 5 => 7, 8 => 10 }
     */
    static mapUniquePrioritiesToIntegers(sortedPriorities) {
        const mapping = new Map();
        if (sortedPriorities.length === 0) {
            return mapping;
        }
        let previousMappedValue = 0;
        let previousOriginalValue = 0;
        for (const priority of sortedPriorities) {
            const quantizedDelta = Math.ceil(priority - previousOriginalValue);
            const mappedValue = previousMappedValue + quantizedDelta;
            mapping.set(priority, mappedValue);
            previousMappedValue = mappedValue;
            previousOriginalValue = priority;
        }
        return mapping;
    }
    /**
     * Applies exponential spacing to a priority value.
     *
     * Uses the formula: Math.floor(value ** EXPONENTIAL_SPACING_FACTOR)
     *
     * This creates exponentially-spaced values that DeepPCB prefers for better routing.
     * For example: 1 => 1, 2 => 5, 3 => 15, 4 => 32, 5 => 55, 6 => 88
     */
    static applyExponentialSpacing(value) {
        return Math.floor(Math.pow(value, AliffPriorityMapper.EXPONENTIAL_SPACING_FACTOR));
    }
    /**
     * Transforms Flux auto-layout priorities to DeepPCB-compatible routing priorities.
     *
     * Complete transformation pipeline:
     * 1. Collects all unique priority values
     * 2. Maps them to sequential integers (preserving order and gaps)
     * 3. Applies exponential spacing (EXPONENTIAL_SPACING_FACTOR)
     * 4. Inverts values so lower Flux priority => higher DeepPCB priority
     *
     * @param netPriorities Map of net ID to Flux auto-layout priority
     * @returns Map of net ID to transformed DeepPCB routing priority
     */
    static transformPrioritiesForDeepPcb(netPriorities) {
        if (netPriorities.size === 0) {
            return new Map();
        }
        // Step 1: Collect all unique priority values and sort them
        const uniquePriorities = Array.from(new Set(netPriorities.values())).sort((a, b) => a - b);
        // Step 2: Map to sequential integers
        const integerMapping = AliffPriorityMapper.mapUniquePrioritiesToIntegers(uniquePriorities);
        // Step 3: Apply exponential spacing to all mapped integers
        const spacedMapping = new Map();
        for (const [originalPriority, mappedInteger] of integerMapping.entries()) {
            const spacedValue = AliffPriorityMapper.applyExponentialSpacing(mappedInteger);
            spacedMapping.set(originalPriority, spacedValue);
        }
        // Step 4: Find the maximum spaced value for inversion
        const maxSpacedValue = Math.max(...Array.from(spacedMapping.values()));
        // Step 5: Invert and map back to net IDs
        const transformedPriorities = new Map();
        for (const [netId, originalPriority] of netPriorities.entries()) {
            const spacedValue = spacedMapping.get(originalPriority);
            if (spacedValue !== undefined) {
                // Invert: lower Flux priority (higher number) => higher DeepPCB priority (lower number)
                const invertedValue = maxSpacedValue - spacedValue;
                transformedPriorities.set(netId, invertedValue);
            }
        }
        return transformedPriorities;
    }
}
//# sourceMappingURL=AliffPriorityMapper.js.map